import { useCallback, useContext, useEffect, useState } from "react";
import API from "./API";
import ModalUserAddEditDayEntry from "./Modal/UserAddEditDayEntry";
import UserAddMultipleDayEntries from "./Modal/UserAddMultipleDayEntries";
import { ModalContext } from './ModalContext';
import { DayAllocated, UserDeleteEntryType, UserUpdateEntryType, UserUpdateRowType } from "./interfaces";
import { InitData, QueryActivities, QueryTimeReportRows, ReportingData, ReportingDataDay, SaveDayResponse } from '../../backend/src/api/dto'
import DisplayTimeReportState from "./components/DisplayTimeReportState";
import ModalConfirmation from "./Modal/Confirmation";
import UserAddTimeReportRow from "./Modal/UserAddTimeReportRow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import isToday from "dayjs/plugin/isToday";
import dayjs from "dayjs";
import { weekDays } from "./constants";
dayjs.extend(isToday);

type FormSwitchValues = {
    showNonWorkingDays: boolean
}


// users level=0 are using this component

function MonthlyReporting() {

    const [showModal, closeModal] = useContext(ModalContext);

    const [initData, setInitData] = useState<InitData>({
        projects: [],
        activities: [],
        specialProjects: []
    });
    const [currentData, setCurrentData] = useState<ReportingData>({
        month: 0,
        year: 0,
        projects: [],
        days: [],
        loggedHours: [],
        daysInMonth: 0,
        previousMonth: 0,
        nextMonth: 0,
        previousYear: 0,
        nextYear: 0,
        timeReport: null,
        workDays: 0,
        workHours: 0,
        allocatedHours: 0,
        hoursToAllocate: 0,
        overtimeHours: 0,
        maxDayHours: 0,
    })
    const [loading, setLoading] = useState(true)
    const [dayAllocated, setDayAllocated] = useState<DayAllocated>({})
    const [formData, setFormData] = useState<FormSwitchValues>({
        showNonWorkingDays: false
    })

    useEffect(() => {
        Promise.all([
            loadData(currentData.month, currentData.year),
            API.userInitVariables().then((data: InitData) => {
                setInitData(data)
                return Promise.resolve(null)
            })
        ]).then(() => {
            setLoading(false)
        })
    }, [])

    useEffect(() => {
        if (currentData.loggedHours) {
            setDayAllocated(
                currentData.loggedHours.reduce((acc: DayAllocated, item) => {
                    if (!acc[item.day]) {
                        acc[item.day] = 0;
                    }
                    acc[item.day] += item.hours ?? 0
                    return acc
                }, { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0, 11: 0, 12: 0, 13: 0, 14: 0, 15: 0, 16: 0, 17: 0, 18: 0, 19: 0, 20: 0, 21: 0, 22: 0, 23: 0, 24: 0, 25: 0, 26: 0, 27: 0, 28: 0, 29: 0, 30: 0, 31: 0 })
            );

        } else {
            setDayAllocated({});
        }
    }, [currentData])


    const loadData = (m: number | null, y: number | null) => {
        setLoading(true)
        return API.userGetReporting(m, y).then((data: ReportingData) => {
            setCurrentData({
                ...data,
                days: data.days.map(item => {
                    item.d = dayjs(item.d)
                    return item
                })
            })
        })
    }

    const changeMonth = (m: number | null, y: number | null) => {
        return loadData(m, y).then(() => {
            setLoading(false)
        })
    }


    const updateEntry = (day: number, timeReportRowId: number, hours: number, timeReportEntryId: number, activityId: number) => {
        setCurrentData(prevData => {
            const loggedHours = prevData.loggedHours.find((item) => {
                return (item.timeReportRowId === timeReportRowId && item.day === day && item.activityId === activityId)
            }) ? prevData.loggedHours.map((item) => {
                if (item.timeReportRowId === timeReportRowId && item.day === day && item.activityId === activityId) {
                    item.hours = hours
                }
                return item
            }) : [...prevData.loggedHours, {
                day,
                timeReportRowId,
                hours,
                timeReportEntryId,
                activityId ///////////////////////////////////////
            }];
            const allocatedHours = loggedHours.reduce((acc, item) => {
                return acc += item.hours
            }, 0)
            const hoursToAllocate = (prevData.workHours - allocatedHours);

            return {
                ...prevData,
                loggedHours,
                allocatedHours,
                hoursToAllocate: hoursToAllocate > 0 ? hoursToAllocate : 0,
                overtimeHours: hoursToAllocate < 0 ? Math.abs(hoursToAllocate) : 0,
            }
        })

    }

    const deleteEntry = (day: number, timeReportRowId: number, activityId: number) => {
        setCurrentData(prevData => {
            const loggedHours = prevData.loggedHours.filter((item) => {
                return !(item.timeReportRowId === timeReportRowId && item.day === day && item.activityId === activityId)
            })
            const allocatedHours = loggedHours.reduce((acc, item) => {
                return acc += item.hours
            }, 0)
            const hoursToAllocate = (prevData.workHours - allocatedHours);
            return {
                ...prevData,
                loggedHours,
                allocatedHours,
                hoursToAllocate: hoursToAllocate > 0 ? hoursToAllocate : 0,
                overtimeHours: hoursToAllocate < 0 ? Math.abs(hoursToAllocate) : 0,
            }
        })
    }
    const switchValue = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prevData => {
            return {
                ...prevData,
                [e.target.name]: e.target.checked
            }
        })
    }

    const sendToClose = () => {
        setLoading(true)
        return API.monthActions({
            month: currentData.month,
            year: currentData.year,
            action: 'close'
        }).then((data: ReportingData) => {
            setCurrentData({
                ...data,
                days: data.days.map(item => {
                    item.d = dayjs(item.d)
                    return item
                })
            })
            setLoading(false)
        })
    }


    const updateRow = (data: SaveDayResponse[]) => {
        data.forEach(item => {
            updateEntry(item.day, item.timeReportRowId, item.hours, item.timeReportEntryId, item.activityId)
        })

    }

    const reloadData = (): Promise<any> => {
        return Promise.resolve()
    }

    const addFunction = (orderId: string) => {
        setLoading(true)
        return API.monthActions({
            month: currentData.month,
            year: currentData.year,
            orderId,
            action: 'addRow'
        }).then((data: ReportingData) => {
            setCurrentData(data)
            setLoading(false)
        })
    }





    if (loading) {
        return (
            <div className="p-3">
                <div className="row mb-3">
                    <div className="col-4">
                        <div className="btn-group" role="group">
                            <button className="btn btn-primary" disabled >&laquo;</button>
                            <button className="btn btn-primary" disabled style={{ width: '100px' }}>Nahrávam</button>
                            <button className="btn btn-primary" disabled>&raquo;</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="p-3">
            <div className="row mb-3">
                <div className="col-4">
                    <div className="btn-group" role="group">
                        <button className="btn btn-primary" onClick={() => changeMonth(currentData.previousMonth!, currentData.previousYear!)}>&laquo;</button>
                        <button className="btn btn-primary" style={{ width: '100px' }}>{currentData.month} . {currentData.year}</button>
                        <button className="btn btn-primary" onClick={() => changeMonth(currentData.nextMonth!, currentData.nextYear!)}>&raquo;</button>
                    </div>
                </div>
                <div className="col-4">Stav: <DisplayTimeReportState state={currentData.timeReport?.status} /></div>
                <div className="col-4">
                    <div className="form-check form-switch float-end">
                        <input onChange={switchValue} name="showNonWorkingDays" className="form-check-input" type="checkbox" role="switch" />
                        <label className="form-check-label" >Skryť nepracovné dni</label>
                    </div>
                </div>
            </div>
            {
                currentData.timeReport ?
                    <ReportingTable
                        currentData={currentData}
                        initData={initData}
                        dayAllocated={dayAllocated}
                        formData={formData}
                        updateEntry={updateEntry}
                        deleteEntry={deleteEntry}
                        updateRow={updateRow}
                    />
                    : <div className="alert alert-warning" role="alert"><FontAwesomeIcon fixedWidth size="2x" icon={["fas", "exclamation-triangle"]} /> Pre tento mesiac ešte nebol vygenerovaný timereport</div>
            }
            {
                currentData.timeReport && currentData.timeReport?.status === 0 ?
                    <button className="btn btn-success btn-sm" onClick={
                        () => showModal(
                            <UserAddTimeReportRow
                                item={null}
                                reload={reloadData}
                                alreadyAdded={currentData.projects}
                                addFunction={addFunction}
                                initData={initData}
                            />
                        )

                    }><FontAwesomeIcon fixedWidth icon={["fas", "plus"]} /> Pridať riadok</button>
                    : null
            }

            <div className="row mt-5">
                <div className="col-7">

                    <table className="table table-bordered">
                        <thead>
                            <tr>
                                <th colSpan={2} className="text-center bg-cyan">Informácie</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* <tr>
                                <td>Druh osoby</td>
                                <td>-----</td>
                            </tr>
                            <tr>
                                <td>Schvaľovateľ</td>
                                <td>-----</td>
                            </tr> */}
                            <tr>
                                <td>Pracovný úväzok</td>
                                <td><DisplayHours hours={currentData.timeReport?.stdDayHours ?? null} />/deň</td>
                            </tr>
                            <tr>
                                <td>Max. alokácia</td>
                                <td><DisplayHours hours={currentData.timeReport?.maxDayHours ?? null} />/deň</td>
                            </tr>
                            <tr>
                                <td>Povolené nadčasy</td>
                                <td>{
                                    currentData.timeReport?.overtimeHours ?
                                        //<span><DisplayHours hours={currentData.timeReport?.overtimeHours ?? null} /> / mesiac</span>
                                        'Ano'
                                        : 'Nie'
                                }</td>
                            </tr>
                            <tr>
                                <td>Alokácie v nepracovný deň</td>
                                <td>{currentData.timeReport?.nonWorkingDayEntries ? 'Áno' : 'Nie'}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div className="col-5">

                    <table className="table table-bordered">
                        <thead>
                            <tr>
                                <th className="text-center bg-cyan" colSpan={2}>Sumarizácia</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Fond pracovného času</td>
                                <td className="text-center" title={currentData.workDays + " dni  x  " + currentData.timeReport?.stdDayHours + " hodin"}>{currentData.workDays ? <DisplayHours hours={currentData.workHours} /> : null}</td>
                            </tr>
                            <tr>
                                <td>Alokované</td>
                                <td className="text-center"><DisplayHours hours={currentData.allocatedHours} /></td>
                            </tr>
                            <tr>
                                <td>Alokovať</td>
                                <td className="text-center"><DisplayHours hours={currentData.hoursToAllocate} /></td>
                            </tr>
                            {
                                currentData.timeReport?.overtimeHours ?
                                    <tr>
                                        <td>Nadčasy</td>
                                        <td className="text-center"><DisplayHours hours={currentData.overtimeHours} /></td>
                                    </tr>
                                    : null
                            }

                        </tbody>
                    </table>
                </div>


            </div>

            <div className="row mt-5">
                <div className="col text-end">
                    {
                        currentData.timeReport?.status === 0 ?
                            <button className="btn btn-success"

                                onClick={
                                    () => showModal(
                                        <ModalConfirmation
                                            confirm={sendToClose}
                                            title={"Odoslať na potvrdenie?"}
                                            text="" />
                                    )
                                }
                            ><FontAwesomeIcon fixedWidth icon={["fas", "envelope-circle-check"]} /> Odoslať na potvrdenie</button>
                            : null
                    }
                </div>
            </div>

        </div>
    )
}


function ReportingTable({
    currentData,
    initData,
    dayAllocated,
    formData,
    updateEntry,
    deleteEntry,
    updateRow,
}: {
    currentData: ReportingData,
    initData: InitData,
    dayAllocated: DayAllocated,
    formData: FormSwitchValues,
    updateEntry: UserUpdateEntryType,
    deleteEntry: UserDeleteEntryType
    updateRow: UserUpdateRowType,
}) {
    const [showModal, closeModal] = useContext(ModalContext);
    const [hover, setHover] = useState<number | null>(null)

    const getLoggedHour = useCallback((timeReportRowId: number, day: number, withActivities: boolean = false, activity?: any) => {
        if (!currentData.loggedHours) {
            return null
        }
        const foundItem = currentData.loggedHours.find(item => {
            if (withActivities) {
                return item.day === day && item.timeReportRowId === timeReportRowId && item.activityId === activity.activityId
            } else {
                return item.day === day && item.timeReportRowId === timeReportRowId
            }
        })
        if (foundItem) {
            return foundItem.hours
        }
        return null
    }, [currentData])


    const getActivities = useCallback((item: QueryTimeReportRows) => {

        const loggedActivities = currentData.loggedHours.reduce((acc, entry) => {

            if (item.timeReportRowId === entry.timeReportRowId) {
                const found = initData.activities.find((act) => act.activityId === entry.activityId);
                if (found) {
                    if (acc.findIndex(it => it.activityId === entry.activityId) === -1) {
                        acc.push(found)
                    }
                }
            }
            return acc

        }, [] as QueryActivities[])

        return loggedActivities.length ? loggedActivities : [{ activityId: 0, name: '' }]
    }, [currentData])


    const getRowLoggedHours = useCallback((timeReportRowId: number, activityId: number) => {
        return currentData.loggedHours.reduce((acc, item) => {
            if (item.timeReportRowId === timeReportRowId && item.activityId === activityId) {
                acc += item.hours
            }
            return acc
        }, 0)
    }, [currentData])



    return (


        <table className="table table-bordered table-condensed">
            <thead>
                <tr>
                    <th style={{ fontSize: '12px' }}>Projekt</th>
                    <th style={{ fontSize: '12px' }}>Komponent</th>
                    <th style={{ fontSize: '12px' }} className="border-right">Aktivita</th>
                    {
                        currentData.days.map((day, index) => {

                            let classes = ['day text-center'];

                            if (hover && hover === index + 1) {
                                classes.push("bg-cyan")
                            }
                            if (day.d.isToday()) {
                                classes.push("today")
                            }
                            if (day.dayType === 1 || day.dayType === 2) {
                                classes.push('text-danger bg-light-pink');
                            }

                            if (!formData.showNonWorkingDays || (formData.showNonWorkingDays && day.dayType === 0)) {
                                return (
                                    <th role="button" onMouseEnter={() => setHover(index + 1)} onMouseLeave={() => setHover(null)} className={classes.join(' ')} style={{ fontSize: '12px', width: '25px' }}><div className="day-name">{weekDays[day.dayOfWeek].name2}</div><div className="day-number">{index + 1}</div></th>
                                )
                            } else {
                                return null
                            }
                        })

                    }
                    <th className="text-center" style={{ fontSize: '12px', width: '50px' }}>Sumár</th>
                </tr>

            </thead>
            <tbody>
                {
                    currentData.projects.map((item) => {
                        const activities = item.withActivities ? getActivities(item) : [{ activityId: 0, name: '' }];
                        return (
                            <>
                                {
                                    activities.map((activity, index: number) => {
                                        const rowLoggedHours = getRowLoggedHours(item.timeReportRowId, activity.activityId)

                                        return (
                                            <tr>
                                                {
                                                    index === 0 ?
                                                        <>
                                                            <td style={{ fontSize: '12px', width: '60px' }} rowSpan={activities.length}>{item.projectName}</td>
                                                            <td style={{ fontSize: '12px', width: '60px' }} rowSpan={activities.length}>{item.orderName}</td>
                                                        </>
                                                        : null
                                                }
                                                {
                                                    item.withActivities ?
                                                        <td style={{ fontSize: '12px', width: '60px' }} className="border-right">{activity.name}</td>
                                                        :
                                                        <td style={{ fontSize: '12px', width: '60px' }} className="border-right bg-light-grey"></td>
                                                }
                                                {
                                                    currentData.days.map((day, index) => {
                                                        const hours = getLoggedHour(item.timeReportRowId, day.day, item.withActivities, activity) ?? null;
                                                        let classes = ['text-center reporting-entry'];
                                                        if (hover && hover === index + 1) {
                                                            classes.push("bg-cyan")
                                                        }

                                                        if (day.d.isToday()) {
                                                            classes.push("today")
                                                        }


                                                        if (!formData.showNonWorkingDays || (formData.showNonWorkingDays && day.dayType === 0)) {
                                                            if (!currentData.timeReport?.nonWorkingDayEntries && day.dayType !== 0) {

                                                                classes.push('text-danger bg-light-pink pointer-not-allowed');

                                                                return (
                                                                    <td className={classes.join(' ')}></td>
                                                                )
                                                            } else {

                                                                if (currentData.timeReport?.status === 0) {


                                                                    return (
                                                                        <td role="button" className={classes.join(' ')}
                                                                            onClick={
                                                                                () => showModal(
                                                                                    <ModalUserAddEditDayEntry
                                                                                        item={item}
                                                                                        day={day}
                                                                                        activity={activity}
                                                                                        hours={hours}
                                                                                        initData={initData}
                                                                                        currentData={currentData}
                                                                                        dayAllocated={dayAllocated}
                                                                                        updateEntry={updateEntry}
                                                                                        deleteEntry={deleteEntry}
                                                                                    />
                                                                                )
                                                                            }
                                                                        >{hours}  </td>
                                                                    )
                                                                } else {

                                                                    classes.push('pointer-not-allowed');

                                                                    return (
                                                                        <td className={classes.join(' ')}>{hours}</td>
                                                                    )

                                                                }
                                                            }
                                                        } else {
                                                            return null
                                                        }
                                                    })
                                                }
                                                {
                                                    currentData.timeReport?.status === 0 ?
                                                        <td role="button" className="text-center reporting-entry" style={{ width: '60px' }} onClick={
                                                            () => showModal(
                                                                <UserAddMultipleDayEntries
                                                                    item={item}
                                                                    activity={activity}
                                                                    initData={initData}
                                                                    currentData={currentData}
                                                                    dayAllocated={dayAllocated}
                                                                    updateRow={updateRow}
                                                                />
                                                            )
                                                        }>{rowLoggedHours}</td>
                                                        :
                                                        <td className="text-center reporting-entry pointer-not-allowed">{rowLoggedHours}</td>
                                                }

                                            </tr>
                                        )
                                    })
                                }
                            </>
                        )
                    })
                }

            </tbody>
            <tfoot>
                <tr>
                    <td style={{ fontSize: '12px' }} className="text-center border-right" colSpan={3}>CELKOVO ALOKOVANÉ</td>
                    {
                        currentData.days.map((day, index) => {
                            let classes = ["text-center fw-bold"]

                            if (day.d.isToday()) {
                                classes.push("today")
                            }
                            if (!formData.showNonWorkingDays || (formData.showNonWorkingDays && day.dayType === 0)) {
                                if (day.dayType === 1 || day.dayType === 2) {
                                    classes.push('text-danger bg-light-pink');
                                }

                                return (
                                    <td className={classes.join(' ')} style={{ fontSize: '12px' }}><AllocationStatus currentData={currentData} dayAllocated={dayAllocated} day={day} /></td>
                                )
                            }
                        })
                    }
                    <td className="text-center" style={{ fontSize: '12px', width: '60px' }}>{currentData.allocatedHours}</td>
                </tr>
            </tfoot>
        </table>
    )


}


function DisplayHours({ hours }: { hours: number | null }) {
    return (
        <span>{hours} hod.</span>
    )
}


function AllocationStatus({ currentData, dayAllocated, day }: { currentData: ReportingData, dayAllocated: DayAllocated, day: ReportingDataDay }) {
    if (day.dayType === 0) {
        let classes = [];
        if (currentData.timeReport?.stdDayHours === dayAllocated[day.day]) {
            classes.push('text-success');
        } else if ((currentData.timeReport?.stdDayHours ?? 0) < dayAllocated[day.day]) {
            classes.push('text-warning');
        } else {
            classes.push('text-danger')
        }

        return (
            <span className={classes.join(' ')}>{dayAllocated[day.day]}</span>
        )
    } else {
        return null
    }
}

// function DisplayNoOrHours({data}: {data: number | undefined}) {
//     if (!data) {
//         return (
//             <span>Nie</span>
//         )
//     }
//     return (
//         <DisplayHours hours={data} />
//     )
// }


export default MonthlyReporting;

